import { LABEL_LIST_MIN_Y_GAP } from '../../shared/AppConstants';

export const create2DArray = (
  numRows,
  numCols,
  defaultCellObject = {
    data: undefined,
    position: undefined
  }
) => {
  const array = new Array(numRows);

  for (let i = 0; i < numRows; i++) {
    array[i] = new Array(numCols);
    for (let j = 0; j < numCols; j++) {
      // eslint-disable-next-line object-curly-newline
      array[i][j] = { ...defaultCellObject };
    }
  }
  return array;
};

export const updateSuggestedLabelListXCoords = (
  suggestedYCoords,
  currentLabelListProps
) => {
  const {
    datasetIndex: curDatasetIndex,
    index: curBarIndex,
    isDisplay,
    value: curYData,
    y: curYPos
  } = currentLabelListProps;

  suggestedYCoords[curDatasetIndex][curBarIndex].data = curYData;

  if (!isDisplay) return [...suggestedYCoords];

  const findNearestYCoordWithSameDataSign = () => {
    const data = suggestedYCoords[curDatasetIndex][curBarIndex].data;

    for (let i = curDatasetIndex - 1; i >= 0; i--) {
      const prevData = suggestedYCoords[i][curBarIndex].data;

      const prevYPos = suggestedYCoords[i][curBarIndex].position;

      if (prevData * data > 0 && prevYPos != null)
        return suggestedYCoords[i][curBarIndex];
    }
    return null;
  };

  const nearestYCoord = findNearestYCoordWithSameDataSign();

  if (!nearestYCoord) {
    suggestedYCoords[curDatasetIndex][curBarIndex].position =
      curYPos + (curYData >= 0 ? 0 : LABEL_LIST_MIN_Y_GAP);
    return [...suggestedYCoords];
  }

  let isCurPosBelowNearestPos, curGap, gapSize;

  if (curYData >= 0) {
    isCurPosBelowNearestPos = curYPos >= nearestYCoord.position;
    curGap = nearestYCoord.position - curYPos;
    gapSize = -LABEL_LIST_MIN_Y_GAP;
  } else {
    isCurPosBelowNearestPos = curYPos < nearestYCoord.position;
    curGap = curYPos - nearestYCoord.position;
    gapSize = LABEL_LIST_MIN_Y_GAP;
  }

  // current is "below" nearest
  if (isCurPosBelowNearestPos) {
    suggestedYCoords[curDatasetIndex][curBarIndex].position =
      nearestYCoord.position + gapSize;
    return [...suggestedYCoords];
  }

  // current is "above" nearest

  // current gap size is smaller than predefined threshold
  if (curGap < LABEL_LIST_MIN_Y_GAP) {
    suggestedYCoords[curDatasetIndex][curBarIndex].position = curYPos + gapSize;
    return [...suggestedYCoords];
  }

  // current gap size is not smaller than predefined threshold
  suggestedYCoords[curDatasetIndex][curBarIndex].position = curYPos;
  return [...suggestedYCoords];
};

export const sliceDataForScreensizes = (array, width) => {
  let final = array;

  if (width <= 240) {
    final = final.slice(-4);
  } else if (width <= 260) {
    final = final.slice(-5);
  } else if (width <= 300) {
    final = final.slice(-6);
  } else if (width <= 350) {
    final = final.slice(-7);
  }

  return final;
};

export const kFormatter = (num) =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact'
  }).format(num);

/**
 * Formats a number as a compact currency string with sign support.
 * e.g. 1_500_000 → "$1.5m", -3_000 → "-$3k"
 */
export const formatCurrencyCompact = (value) => {
  if (value == null) return '';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000_000)
    return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

/**
 * Computes evenly-spaced, "nice" Y-axis tick values and domain for a chart.
 * @param {number} minVal - Minimum data value.
 * @param {number} maxVal - Maximum data value.
 * @param {number} [targetTickCount=5] - Desired number of tick marks.
 * @returns {{ ticks: number[], domain: [number, number] }}
 */
export const getYAxisConfig = (minVal, maxVal, targetTickCount = 5) => {
  if (minVal === 0 && maxVal === 0) return { ticks: [0], domain: [0, 1] };

  const range = maxVal - minVal || Math.abs(maxVal) || 1;
  const rawStep = range / targetTickCount;

  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;
  let niceStep;
  if (residual <= 1.5) niceStep = 1 * magnitude;
  else if (residual <= 3.5) niceStep = 2 * magnitude;
  else if (residual <= 7.5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  const niceMin = Math.floor(minVal / niceStep) * niceStep;
  const niceMax = Math.ceil(maxVal / niceStep) * niceStep;

  const ticks = [];
  for (let v = niceMin; v <= niceMax; v += niceStep) {
    ticks.push(Math.round(v));
  }

  return { ticks, domain: [niceMin, niceMax] };
};
